/* eslint-disable max-lines */

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { merge as observableMerge, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { noWhitespaceValidator } from '@recruitee/common';
import { Attribute, AttributeListData } from '@recruitee/http-types';
import { LifeCycle } from '@recruitee/utils';

import { DropdownHeaderComponent } from '../../common/dropdown/dropdown-header/dropdown-header.component';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent } from '../../common/dropdown/dropdown.component';
import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import { HotkeysService } from '../../core/a11y/hotkeys.service';
import { KeyCode } from '../../core/core.module';
import { FormFieldComponent } from '../../forms/form-field/form-field.component';
import { InputComponent } from '../../forms/input/input.component';
import { AttributesSelectListComponent } from '../attributes-select-list/attributes-select-list.component';
import { AttributesLoadMore, AttributesTranslationsType } from '../attributes.types';

@Component({
  selector: 'rt-attributes-select',
  templateUrl: './attributes-select.component.html',
  styleUrls: ['./attributes-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.width]': 'width',
  },
  standalone: true,
  imports: [
    AttributesSelectListComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    FormFieldComponent,
    IconColorDirective,
    IconComponent,
    InputComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
})
export class AttributesSelectComponent {
  public readonly CREATE_NEW_ITEM_ID: string = 'create_new';
  public readonly defaultControl: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    noWhitespaceValidator,
  ]);

  @Input() public canPick: boolean = false;
  @Input() public canCreate: boolean = false;
  @Input() public createDisabled: boolean = false;

  @Input() public resetOnSaveFromInput: boolean = true;
  @Input() public resetOnSaveFromSuggestion: boolean = false;

  @Input() public loading: boolean = false;
  @Input() public width: string;

  @Input() public selectFooterTemplate: TemplateRef<any>;
  @Input() public selectHeaderTemplate: TemplateRef<any>;
  @Input() public hasSearch: boolean = true;

  // eslint-disable-next-line
  @Input() public set suggestions(suggestions: AttributeListData) {}

  // To disallow using the input directly, since it is the same type as (the correct one) activeSuggestions
  public get suggestions(): never {
    return <never>this.activeSuggestions;
  }

  @Input() public showAlreadyExists = false;
  @Input() public maxDropdownHeight: string;
  @Input() public maxDropdownWidth: string;

  @Input() public text: AttributesTranslationsType;
  @Input() public header?: string;

  @Input() public initialValue: string = '';
  @Input() public maxLength: number;

  @Output() public dispose: EventEmitter<void> = new EventEmitter();
  @Output() public save: EventEmitter<Attribute> = new EventEmitter();
  @Output() public query: EventEmitter<string> = new EventEmitter<string>();
  @Output() public loadMore = new EventEmitter<AttributesLoadMore>();

  @ViewChild('createItemTpl') public createItemTemplate: TemplateRef<any>;
  @ViewChild('input') public rtInput: InputComponent;

  @Input() public control: UntypedFormControl;

  @Output() public goToSettings = new EventEmitter<void>();

  public activeSuggestions: AttributeListData = {
    isLoading: false,
    meta: null,
    rows: [],
  };
  public errors: ValidationErrors | null = null;
  public highlight: { id: number | string; index: number } | null = null;
  public preventCreateMessageVisible: boolean = false;

  private rawSuggestions: AttributeListData = { isLoading: false, meta: null, rows: [] };

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Observable<void>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private hotkeys: HotkeysService,
  ) {
    this.handleKeyboardNavigation();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.control) {
      this.control = this.defaultControl;
    }
    if ('showAlreadyExists' in changes) {
      this.updateSuggestions(this.rawSuggestions);
    }
    if ('suggestions' in changes) {
      this.updateSuggestions(changes.suggestions.currentValue);
    }
    if ('maxLength' in changes) {
      // there is no getValidators() method
      // https://github.com/angular/angular/issues/13461
      const existingValidators = [Validators.required, noWhitespaceValidator];
      this.control.setValidators([
        ...existingValidators,
        Validators.maxLength(changes.maxLength.currentValue),
      ]);
      if (this.control.value) this.control.updateValueAndValidity();
    }
  }

  public keydownEscape($event: Event): void {
    $event.preventDefault();

    if (this.control.valid) {
      this.control.reset();
    } else {
      this.dispose.emit();
    }
  }

  public ngOnInit(): void {
    this.control.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.query.emit(this.normalizedControlValue);
      this.errors = this.control.errors;
      this.preventCreateMessageVisible = !!this.preventCreateMessageVisible;
      this.cdRef.detectChanges();
    });

    // this will emit `query` update
    if (this.control === this.defaultControl) {
      this.control.setValue(this.initialValue);
    }
    this.control.markAsPristine();
  }

  public onSaveAttribute(attr: Attribute, saveOrigin: 'suggestion' | 'input'): void {
    this.saveAttribute(attr, saveOrigin);
    if (this.rtInput) {
      this.rtInput.setFocus();
    }
  }

  public onLoadMore(): void {
    const { limit, page } = this.activeSuggestions.meta!;
    this.loadMore.emit({
      limit,
      page: page + 1,
      query: this.control.value,
    });
  }

  private saveAttribute(attr: Attribute, saveOrigin: 'suggestion' | 'input'): void {
    if (this.showAlreadyExists && saveOrigin === 'input') {
      return;
    }

    if (
      (saveOrigin === 'suggestion' && this.resetOnSaveFromSuggestion) ||
      (saveOrigin === 'input' && this.resetOnSaveFromInput)
    ) {
      this.control.reset('', { emitEvent: true, onlySelf: true });
    }

    this.save.emit(attr);
  }

  private get normalizedControlValue(): string {
    return this.control.value === null ? '' : this.control.value.trim().toLowerCase();
  }

  private get highlighted(): Attribute | null {
    return this.activeSuggestions.rows.find(attr => attr.id === this.highlight?.id) ?? null;
  }

  private handleKeyboardNavigation(): void {
    this.hotkeys
      .shortcut([KeyCode.Enter], false)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        const highlighted = this.highlighted;
        if (!this.canPick) {
          return;
        }

        if (highlighted) {
          this.saveAttribute(highlighted, 'suggestion');
          return;
        }

        if (!this.canCreate) {
          if (this.activeSuggestions.rows.length > 0 && this.control.valid) {
            this.preventCreateMessageVisible = true;
            this.cdRef.detectChanges();
          }
          return;
        }

        if (this.control.valid) {
          this.saveAttribute({ value: this.control.value }, 'input');
        }
      });

    observableMerge<Array<-1 | 1>>(
      this.hotkeys.shortcut([KeyCode.UpArrow], false).pipe(map(() => -1)),
      this.hotkeys.shortcut([KeyCode.DownArrow], false).pipe(map(() => 1)),
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(delta => this.moveHighlight(delta));
  }

  private moveHighlight(delta: 1 | -1 | 0): void {
    const suggestions = this.activeSuggestions;
    const highlight = this.highlight;

    if (suggestions.rows.length === 0) {
      return;
    }

    let nextHighlight: typeof this.highlight;

    if (!highlight) {
      nextHighlight = {
        id: (delta === 1 ? suggestions.rows[0] : suggestions.rows[suggestions.rows.length - 1]).id!,
        index: delta === 1 ? 0 : suggestions.rows.length - 1,
      };
      return;
    } else {
      const nextIndex = highlight.index + delta;
      nextHighlight = suggestions.rows[nextIndex]
        ? {
            id: suggestions.rows[nextIndex].id!,
            index: nextIndex,
          }
        : delta === 1
          ? {
              id: suggestions.rows[0].id!,
              index: 0,
            }
          : {
              id: suggestions.rows[suggestions.rows.length - 1].id!,
              index: suggestions.rows.length - 1,
            };
    }

    this.highlight = nextHighlight;
    this.cdRef.detectChanges();
  }

  // eslint-disable-next-line max-lines-per-function
  private updateSuggestions(suggestions: AttributeListData): void {
    // reset highlight if there are no results
    let nextHighlight = null;
    let createItemVisible = false;
    let preventCreateMessageVisible = this.preventCreateMessageVisible;

    const normalizedControlValue = this.normalizedControlValue;

    if (this.canCreate && suggestions.rows.length > 0 && this.control.valid) {
      const inputValueExactMatch = this.findAttributeByInputValue(
        normalizedControlValue,
        suggestions.rows,
      );

      if (!inputValueExactMatch) {
        createItemVisible = true;
      }
    }

    const nextSuggestions = {
      ...suggestions,
      rows:
        createItemVisible && !this.showAlreadyExists
          ? [...suggestions.rows, { id: this.CREATE_NEW_ITEM_ID, value: normalizedControlValue }]
          : suggestions.rows,
    };

    if (nextSuggestions.rows.length === 1) {
      if (this.canCreate || suggestions.rows[0].value === normalizedControlValue) {
        nextHighlight = {
          id: suggestions.rows[0].id!,
          index: 0,
        };
      } else {
        preventCreateMessageVisible = true;
      }
    }

    if (nextSuggestions.rows.length === 0) {
      preventCreateMessageVisible = false;
    }

    this.preventCreateMessageVisible = preventCreateMessageVisible;
    this.rawSuggestions = suggestions;
    this.highlight = nextHighlight;
    this.activeSuggestions = nextSuggestions;
  }

  private findAttributeByInputValue(
    inputValue: string,
    attributes: Attribute[],
  ): Attribute | undefined {
    return attributes.find(a => a.value.toLowerCase() === inputValue.toLowerCase());
  }
}
